/**
 * setJobPayoutAmount — canonical operational writer for `Job.payoutAmount`.
 *
 * Pure function. Caller owns persistence. The ONLY sanctioned post-creation
 * mutation path for installer compensation outside of:
 *   - Job creation (AppointmentFlow / QuoteDetail / SalesCommandCenter)
 *   - ProjectReviewOverrideDialog (admin governance override — preserved)
 *
 * Guarantees:
 *   - Validates amount (finite, non-negative).
 *   - Role-gated via SET_JOB_PAYOUT_ALLOWED_ROLES.
 *   - Appends an `activityHistory` entry (audit, never silent).
 *   - Emits a canonical `payout_amount_set` SystemEvent.
 *   - No-op (changed: false) when amount is unchanged.
 *   - NEVER touches `financials.contractValue`. The contract sale price and
 *     the installer payout are independent dimensions
 *     (mem://finance/canonical-pricing-write-path).
 */

import type { Job } from "@/data/types";
import type { SystemEvent } from "@/data/communicationEngine";
import type { UserRole } from "@/data/RoleContext";
import { createSystemEvent } from "@/data/eventFactory";

export const SET_JOB_PAYOUT_ALLOWED_ROLES: ReadonlyArray<UserRole> = [
  "super_admin",
  "admin",
  "ops_manager",
  "it_staff",
];

export function canSetJobPayoutAmount(role: UserRole): boolean {
  return SET_JOB_PAYOUT_ALLOWED_ROLES.includes(role);
}

/**
 * Material-divergence threshold for reason requirement (used by UIs).
 * Editors should require a `reason` when |new - baseline| / baseline >= this.
 * Writer itself enforces presence of `reason` ONLY when caller flags it as
 * `materialDivergence: true`; otherwise reason is optional but recorded if
 * provided. UI is the policy layer for divergence detection.
 */
export const PAYOUT_MATERIAL_DIVERGENCE_PCT = 0.1;

export interface SetJobPayoutAmountInput {
  jobId: string;
  amount: number;
  actor: string;
  role: UserRole;
  /** Optional free-text rationale. Recorded verbatim in activity + event. */
  reason?: string;
  /**
   * UI signals that the new amount materially diverges from the baseline
   * the dispatcher saw (e.g. >=10% delta). When true, `reason` MUST be
   * provided (≥10 chars).
   */
  materialDivergence?: boolean;
  /** ISO override for deterministic tests. */
  nowIso?: string;
}

export type SetJobPayoutAmountResult =
  | {
      ok: true;
      jobs: Job[];
      events: SystemEvent[];
      /** True if amount actually changed; false on no-op. */
      changed: boolean;
      previousAmount: number;
      nextAmount: number;
    }
  | {
      ok: false;
      reason:
        | "not_found"
        | "forbidden_role"
        | "invalid_amount"
        | "reason_required";
    };

export function setJobPayoutAmount(
  jobs: Job[],
  input: SetJobPayoutAmountInput,
): SetJobPayoutAmountResult {
  if (!canSetJobPayoutAmount(input.role)) {
    return { ok: false, reason: "forbidden_role" };
  }
  if (
    !Number.isFinite(input.amount) ||
    input.amount < 0 ||
    input.amount > 10_000_000
  ) {
    return { ok: false, reason: "invalid_amount" };
  }

  const target = jobs.find((j) => j.id === input.jobId);
  if (!target) return { ok: false, reason: "not_found" };

  const previousAmount = target.payoutAmount ?? 0;
  const nextAmount = Math.round(input.amount);

  // No-op.
  if (previousAmount === nextAmount) {
    return {
      ok: true,
      jobs,
      events: [],
      changed: false,
      previousAmount,
      nextAmount,
    };
  }

  if (
    input.materialDivergence &&
    (!input.reason || input.reason.trim().length < 10)
  ) {
    return { ok: false, reason: "reason_required" };
  }

  const at = input.nowIso ?? new Date().toISOString();
  const reasonText = input.reason?.trim() || "unspecified";

  const event = createSystemEvent({
    type: "payout_amount_set",
    jobId: input.jobId,
    triggeredBy: input.actor,
    timestamp: at,
    payload: {
      previousAmount,
      nextAmount,
      reason: reasonText,
      materialDivergence: input.materialDivergence === true,
      role: input.role,
    },
  });

  const nextJobs = jobs.map((j) =>
    j.id === input.jobId
      ? {
          ...j,
          payoutAmount: nextAmount,
          activityHistory: [
            ...j.activityHistory,
            {
              id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              date: at,
              action: `Offer compensation set to $${nextAmount.toLocaleString()} (was $${previousAmount.toLocaleString()}). Reason: ${reasonText}`,
              user: input.actor,
              noteType: "system" as const,
            },
          ],
        }
      : j,
  );

  return {
    ok: true,
    jobs: nextJobs,
    events: [event],
    changed: true,
    previousAmount,
    nextAmount,
  };
}
